import React, { useState, useEffect } from "react";
import {
  FileText,
  Calendar,
  MessageCircle,
  Newspaper,
  MessageSquare,
  Mic,
  Briefcase,
  Utensils,
  BookOpen,
  Users,
  TrendingUp,
  Activity,
  Eye,
} from "lucide-react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import englishService from '../services/englishService';
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    articles: 0,
    blogs: 0,
    events: 0,
    news: 0,
    interviews: 0,
    projects: 0,
    speeches: 0,
    sccnMessages: 0,
    ovenfresh: 0,
    subscribers: 0,
    visits: 0,
  });

  const [visitorData, setVisitorData] = useState({
    totalVisits: 0,
    data: [],
    period: "week",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await englishService.getStats();
      console.log("Full response:", response);
      
      // Fix: Use the correct nesting level
      const responseData = response?.data || {};
      
      // Handle nested structure from the API
      const englishData = responseData.english || {};
      const totalsData = responseData.totals || {};
      
      setStats({
        articles: englishData.articles || 0,
        blogs: englishData.blogs || 0,
        events: englishData.events || 0,
        news: englishData.news || 0,
        interviews: englishData.interviews || 0,
        projects: englishData.projects || 0,
        speeches: englishData.speeches || 0,
        sccnMessages: englishData.sccnMessages || 0,
        ovenfresh: englishData.ovenfresh || 0,
        subscribers: totalsData.subscribers || 0,
        visits: totalsData.visits || 0,
      });
      
      console.log("Stats loaded:", responseData);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(err.message || "Failed to fetch statistics. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVisitorData = async (period = "week") => {
    try {
      console.log("Fetching visitor data for period:", period);
      
      const response = await englishService.visits.getByPeriod(period);
      console.log("Raw visitor API response:", response);
      
      if (!response || !response.data) {
        console.error("Invalid API response format:", response);
        setVisitorData({
          totalVisits: 0,
          data: [],
          period,
        });
        return;
      }
      
      // Process and set the visitor data
      setVisitorData({
        totalVisits: response.totalVisits || 0,
        data: Array.isArray(response.data) ? response.data : [],
        period,
      });
      
      console.log("Processed visitor data:", visitorData);
    } catch (err) {
      console.error("Visitor data error:", err);
      setVisitorData({
        totalVisits: 0,
        data: [],
        period,
      });
    }
  };

  useEffect(() => {
    fetchStats();
    fetchVisitorData("week");
  }, []);

  const COLORS = [
    "#2563eb",
    "#059669",
    "#ca8a04",
    "#dc2626",
    "#7c3aed",
    "#0891b2",
    "#be185d",
    "#0d9488",
    "#ea580c",
    "#6366f1",
    "#059669",
  ];

  const statsCards = [
    {
      title: "Articles",
      value: stats?.articles || 0,
      icon: FileText,
      color: "blue",
      path: "/articles",
    },
    {
      title: "Blogs",
      value: stats?.blogs || 0,
      icon: BookOpen,
      color: "green",
      path: "/blogs",
    },
    {
      title: "Events",
      value: stats?.events || 0,
      icon: Calendar,
      color: "yellow",
      path: "/events",
    },
    {
      title: "News",
      value: stats?.news || 0,
      icon: Newspaper,
      color: "red",
      path: "/news",
    },
    {
      title: "Interviews",
      value: stats?.interviews || 0,
      icon: Mic,
      color: "purple",
      path: "/interviews",
    },
    {
      title: "Projects",
      value: stats?.projects || 0,
      icon: Briefcase,
      color: "indigo",
      path: "/projects",
    },
    {
      title: "Speeches",
      value: stats?.speeches || 0,
      icon: MessageCircle,
      color: "pink",
      path: "/speeches",
    },
    {
      title: "SCCN Messages",
      value: stats?.sccnMessages || 0,
      icon: MessageSquare,
      color: "teal",
      path: "/sccn-messages",
    },
    {
      title: "OvenFresh",
      value: stats?.ovenfresh || 0,
      icon: Utensils,
      color: "cyan",
      path: "/ovenfresh",
    },
    {
      title: "Active Subscribers",
      value: stats?.subscribers || 0,
      icon: Users,
      color: "emerald",
      path: "/subscribers",
    },
    {
      title: "Website Visits",
      value: stats?.visits || 0,
      icon: Eye,
      color: "indigo",
      path: "#",
    },
  ];

  const processVisitorChartData = () => {
    console.log("Processing visitor chart data:", visitorData);
    
    if (!visitorData.data || !Array.isArray(visitorData.data) || visitorData.data.length === 0) {
      console.log("No visitor data to process");
      return [];
    }
    
    return visitorData.data.map((item) => {
      const formattedDate = formatDate(item?.date);
      console.log("Processing chart data item:", { date: item?.date, formatted: formattedDate, count: item?.count });
      
      return {
        name: formattedDate,
        visits: item?.count || 0,
      };
    });
  };

  const contentCards = statsCards.filter(
    (card) =>
      card.title !== "Active Subscribers" && card.title !== "Website Visits"
  );
  const pieData = contentCards
    .filter((card) => card.value > 0)
    .map((card) => ({
      name: card.title,
      value: card.value,
    }));

  const totalItems = contentCards.reduce((sum, card) => sum + (card.value || 0), 0);
  const mostPopularType =
    totalItems > 0
      ? contentCards.reduce((a, b) => (a.value > b.value ? a : b))
      : { title: "N/A", value: 0 };

      const formatDate = (date) => {
        if (!date) {
          return "No Date";
        }
        
        try {
          // Ensure date is a Date object
          const d = new Date(date);
          
          // Check if date is valid
          if (isNaN(d.getTime())) {
            console.warn("Invalid date:", date);
            return "Invalid";
          }
          
          switch (visitorData.period) {
            case "day":
              return `${d.getHours()}:00`;
            case "week":
            case "month":
              return `${d.getMonth() + 1}/${d.getDate()}`;
            case "year":
              const months = [
                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
              ];
              return months[d.getMonth()];
            default:
              return `${d.getMonth() + 1}/${d.getDate()}`;
          }
        } catch (e) {
          console.error("Date parsing error:", e, "for date:", date);
          return "Error";
        }
      };

const visitorChartData = processVisitorChartData();

  const handleCardClick = (path) => {
    if (path && path !== "#") navigate(path);
  };

  const handlePeriodChange = (period) => {
    fetchVisitorData(period);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg mb-6 border border-gray-200 mx-6 mt-6">
        <div className="flex flex-col items-center text-center">
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchStats}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Overview</h1>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 transition-colors"
        >
          <Activity className="w-4 h-4" />
          Refresh Stats
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {statsCards.map((card, index) => (
          <div
            key={index}
            onClick={() => handleCardClick(card.path)}
            className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-1 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {card.title}
                </p>
                <h3 className="text-2xl font-bold mt-2 text-gray-800">
                  {(card.value || 0).toLocaleString()}
                </h3>
              </div>
              <card.icon className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        ))}
      </div>

      {/* Visitor Graph Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Website Visitors</h2>
          <div className="flex space-x-2">
            {["day", "week", "month", "year"].map((period) => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`px-3 py-1 rounded-md text-sm ${
                  visitorData.period === period
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          {visitorChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visitorChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip formatter={(value) => [value, "Visitors"]} />
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">
                No visitor data available for this period.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content Distribution Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Content Distribution</h2>
          <div className="h-80">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => (value || 0).toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">No content data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Quick Stats Overview</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Activity className="w-6 h-6 text-blue-500" />
                <span className="font-medium text-gray-700">Total Content Items</span>
              </div>
              <span className="text-xl font-bold text-gray-800">
                {totalItems.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <TrendingUp className="w-6 h-6 text-green-500" />
                <span className="font-medium text-gray-700">Most Popular Type</span>
              </div>
              <div className="text-right">
                <span className="font-medium block text-gray-800">
                  {mostPopularType.title}
                </span>
                <span className="text-sm text-gray-500">
                  {(mostPopularType.value || 0).toLocaleString()} items
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Users className="w-6 h-6 text-purple-500" />
                <span className="font-medium text-gray-700">Active Subscribers</span>
              </div>
              <span className="text-xl font-bold text-gray-800">
                {(stats.subscribers || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Eye className="w-6 h-6 text-indigo-500" />
                <span className="font-medium text-gray-700">Total Website Visitors</span>
              </div>
              <span className="text-xl font-bold text-gray-800">
                {(stats.visits || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;